import { defaultRoleResource, useRoleActions } from '../../../../resource/resources/artifacts/role/roleResource';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { OpenModal } from '../../../../../../react-commons/utils/modals/openModal';
import {
    defaultProcessAreaRoleAssociationResource,
    useProcessAreaRoleAssociationActions,
} from '../../../../resource/resources/artifacts/process_area/processAreaRoleAssociationResource';
import { StackedCardsComponent } from '../../../reusable-components/StackedCardsComponent';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import React, { useState } from 'react';
import { RoleEditDialog } from '../../project/reusable-elements/role/RoleEditDialog';
import { useNestedDialogFunction } from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import {
    defaultProcessAreaRoleRelationResource,
    useProcessAreaRoleRelationActions,
} from '../../../../resource/resources/artifacts/role/processAreaRoleRelationResource';
import { RoleDrawerContent } from './RoleDrawerContent';
import { useDisclosure } from '@mantine/hooks';

export const StackedRolesView = ({isAdmin, processAreaId, versionId, link}: {
    isAdmin: boolean,
    processAreaId: string | undefined,
    versionId: string | undefined,
    link: string | undefined
}) => {

    const nestedDialog = useNestedDialogFunction();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();

    const [opened, {open, close}] = useDisclosure(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

    const roleAssociationActions = useProcessAreaRoleAssociationActions()
    const roleActions = useRoleActions()
    const roleRelationActions = useProcessAreaRoleRelationActions()
    const roleAssociationsQuery = roleAssociationActions.useGetAllResources(processAreaId)
    const rolesQuery = roleActions.useGetAllResources(versionId);
    const roleRelationsQuery = roleRelationActions.useGetAllResources(versionId);

    const deleteRoleAssociation = roleAssociationActions.useDeleteResource();
    const addRoleAssociation = roleAssociationActions.useAddResource();
    const addRole = roleActions.useAddResource();
    const addRoleRelation = roleRelationActions.useAddResource();
    const addAssociation = () => OpenModal.DoubleSelectionOrCreateModal(
        "Select or Create a Role and Relation",
        roleRelationsQuery.data?.map(role => ({label: role.name, value: role.objectId})) ?? [],
        (firstValue: string | null) =>
            rolesQuery.data?.filter(objective =>
                !(roleAssociationsQuery.data
                    ?.some(association => association.role === objective.objectId && association.roleRelation === firstValue)))
                .map(role => ({label: role.name, value: role.objectId})) ?? [],
        "Role Relation",
        "Role",
        true,
        async (firstValue, secondValue) => {
            if (secondValue && processAreaId) {
                await addRoleAssociation.mutateAsync({
                    resource: {
                        ...defaultProcessAreaRoleAssociationResource,
                        role: secondValue,
                        roleRelation: firstValue
                    },
                    parentId: processAreaId
                })
            }
        },
        async (firstValue, secondValue) => {
            if (secondValue && processAreaId && versionId) {
                await addRole.mutateAsync({
                        resource: {
                            ...defaultRoleResource,
                            name: secondValue
                        },
                        parentId: versionId
                    },
                    {
                        onSuccess: (result) => {
                            const roleObjectId = result?.content?.objectId
                            if (roleObjectId) {
                                if (firstValue) {
                                    addRoleRelation.mutateAsync({
                                            resource: {
                                                ...defaultProcessAreaRoleRelationResource,
                                                name: firstValue
                                            },
                                            parentId: versionId
                                        },
                                        {
                                            onSuccess: (result2) => {
                                                const roleRelationObjectId = result2?.content?.objectId
                                                if (roleRelationObjectId) {
                                                    addRoleAssociation.mutateAsync({
                                                        resource: {
                                                            ...defaultProcessAreaRoleAssociationResource,
                                                            role: roleObjectId,
                                                            roleRelation: roleRelationObjectId
                                                        },
                                                        parentId: processAreaId
                                                    })
                                                }
                                            },
                                        }
                                    )
                                } else {
                                    addRoleAssociation.mutateAsync({
                                        resource: {
                                            ...defaultProcessAreaRoleAssociationResource,
                                            role: roleObjectId
                                        },
                                        parentId: processAreaId
                                    })
                                }
                            }
                        },
                    }
                )
            }
        },
        undefined,
        800
    )

    const onDelete = (id: string) => {
        deleteResourceDialogFunction.deleteResource(
            id, null, ResourceType.PROCESS_AREA_ROLE_ASSOCIATION, deleteRoleAssociation
        )
    }

    const onEdit = (id: string) => {
        const association = roleAssociationsQuery.data?.find(value => value.id === id)
        if (association) {
            nestedDialog.showNestedDialog(
                <RoleEditDialog
                    onClose={nestedDialog.closeNestedDialog}
                    objectId={association.role}
                    versionedInId={versionId ?? ""}
                />
            );
        }
    }

    const onView = (_: string, roleId: string|undefined|null) => {
        setSelectedRoleId(roleId ?? null)
        open()
    }

    return (
        <div>
            <StackedCardsComponent
                title={"Roles"}
                data={roleAssociationsQuery.data?.map(value => ({
                    group: roleRelationsQuery.data?.find(resource => resource.objectId === value.roleRelation)?.name ?? null,
                    id: value.id,
                    roleId: rolesQuery.data?.find(resource => resource.objectId === value.role)?.id ?? undefined,
                    text: rolesQuery.data?.find(resource => resource.objectId === value.role)?.name ?? "",
                    description: rolesQuery.data?.find(resource => resource.objectId === value.role)?.description ?? "",
                    img: null
                })) ?? []}
                onAdd={isAdmin ? addAssociation : undefined}
                onDelete={isAdmin ? onDelete : undefined}
                onEdit={isAdmin ? onEdit : undefined}
                onView={onView}
                link={link}
                section={"roles"}
            />
            <RoleDrawerContent
                closeDrawer={() => {
                    setSelectedRoleId(null);
                    close()
                }}
                openedDrawer={opened}
                roleId={selectedRoleId}
            />
        </div>
    );
};